/**
 * Customer Service API Client
 *
 * Frontend service for calling customer-service endpoints
 * Handles Stripe customer data and benefits
 */

import type {
  EligibilityCheck,
  StripeCustomer,
  LinkProfileRequest,
  LinkProfileResponse
} from '@/features/auth/types/customer'

// Use API Gateway (port 3000) - it routes to auth-service internally
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

/**
 * Check if email is an existing Stripe customer
 * WHY: Validate before sending OTP to save email sends
 */
export async function checkEmailEligibility(email: string): Promise<EligibilityCheck> {
  const response = await fetch(`${API_URL}/api/customer/check-eligibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  })

  if (!response.ok && response.status !== 403) {
    throw new Error('Failed to check email eligibility')
  }

  return await response.json()
}

/**
 * Get full Stripe customer data by email
 * WHY: Fetch customer details for welcome modal
 */
export async function getStripeCustomerData(email: string): Promise<StripeCustomer> {
  const response = await fetch(`${API_URL}/api/customer/stripe-data/${encodeURIComponent(email)}`)

  if (!response.ok) {
    throw new Error('Failed to get customer data')
  }

  const result = await response.json()
  return result.data
}

/**
 * Link user profile to Stripe customer data and claim benefits
 * WHY: Connect new profile to legacy data and grant 3 months free
 */
export async function linkProfile(request: LinkProfileRequest): Promise<LinkProfileResponse> {
  const response = await fetch(`${API_URL}/api/customer/link-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    throw new Error(errorData?.error || 'Failed to save profile')
  }

  return await response.json()
}

/**
 * Check premium status for a user
 * WHY: Determine if user has active premium access
 */
export async function getPremiumStatus(userId: string): Promise<{
  hasPremium: boolean
  premiumUntil?: string
  premiumType?: string
}> {
  const response = await fetch(`${API_URL}/api/customer/premium-status/${userId}`)

  if (!response.ok) {
    throw new Error('Failed to check premium status')
  }

  return await response.json()
}

/**
 * Request phone OTP - sends SMS verification code via Twilio
 * WHY: Verify phone number ownership before allowing access
 */
export async function requestPhoneOTP(phoneNumber: string): Promise<{
  message: string
  maskedPhone: string
}> {
  const token = localStorage.getItem('auth_token')
  const response = await fetch(`${API_URL}/api/auth/request-phone-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ phoneNumber })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to send verification code')
  }

  return await response.json()
}

/**
 * Verify phone OTP - checks code and marks phone as verified
 * WHY: Complete phone verification to allow user access
 */
export async function verifyPhoneOTP(
  phoneNumber: string,
  code: string,
  userId: string
): Promise<{
  message: string
  success: boolean
}> {
  const token = localStorage.getItem('auth_token')
  const response = await fetch(`${API_URL}/api/auth/verify-phone-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ phoneNumber, code, userId })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Invalid verification code')
  }

  return await response.json()
}
